import { Box, Button, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ErrorTypes } from './Error.types';
import theme from '../../theme/theme';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

function normalizeErrorKey(message?: string): keyof ErrorTypes {
  if (!message) return 'generic_error' as keyof ErrorTypes;

  const normalized = message
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  return (normalized as keyof ErrorTypes) || ('generic_error' as keyof ErrorTypes);
}

/**
 * Dynamic ErrorFallback
 * Maps the error message to an i18n key automatically.
 * Falls back to genericError if translation key is missing.
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const errorKey = normalizeErrorKey(error.message);

  // Try to translate; fallback to generic message if key is missing
  const translatedMessage = t(`error.types.${String(errorKey)}`, t('error.types.generic_error')); //errorTranslatedMsg to rename

  const handleBackToHome = () => {
    resetError();
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        p: 4,
      }}
    >
      <Typography variant="h4" sx={{ color: theme.palette.primary.main, mb: 2 }}>
        {t('error.ui.title')}
      </Typography>

      <Typography variant="body1" sx={{ color: theme.palette.text.primary, mb: 4, maxWidth: 500 }}>
        {translatedMessage}
      </Typography>

      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={handleBackToHome}
        sx={{ textTransform: 'none' }}
      >
        {t('error.ui.backToHome')}
      </Button>
    </Box>
  );
}
